/**
 * The conversation so far, as the client sees it: short cards, newest last.
 *
 * The entries are the captions `useBusinessAssessment` already merges from
 * LiveKit, so a revised caption updates in place. A caption still being heard
 * is marked as such rather than presented as settled.
 *
 * The list scrolls inside its own box, so following the newest line never
 * scrolls the page away from the controls.
 */

import { useEffect, useRef } from 'react';

export const SPEAKER_LABELS = {
  user: 'You',
  agent: 'NS Assistant',
};

export default function ConversationFeed({ captions }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    const box = scrollRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [captions]);

  return (
    <section className="bd-feed" aria-labelledby="bd-feed-title">
      <h2 id="bd-feed-title" className="bd-feed__title">Conversation</h2>
      <div
        ref={scrollRef}
        className="bd-feed__scroll"
        role="log"
        aria-live="polite"
        aria-relevant="additions text"
        tabIndex={0}
      >
        {captions.length === 0 ? (
          <p className="bd-feed__empty">Your conversation will appear here as you speak.</p>
        ) : (
          <ol className="bd-feed__list">
            {captions.map((caption) => (
              <li
                key={caption.id}
                className={`bd-message bd-message--${caption.role}${
                  caption.final ? '' : ' bd-message--interim'
                }`}
              >
                <span className="bd-message__speaker">{SPEAKER_LABELS[caption.role]}</span>
                <p className="bd-message__text">
                  {caption.text}
                  {!caption.final && (
                    <>
                      <span className="bd-message__pending" aria-hidden="true">…</span>
                      <span className="bd-sr-only"> (still being heard)</span>
                    </>
                  )}
                </p>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}
